"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, CalendarCheck, ShieldCheck, Clock, MapPin, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function RequestService() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  const guarantees = [{
    text: "24/7 Rapid Emergency Response"
  }, {
    text: "Clear, Upfront Pricing"
  }, {
    text: "Licensed & Insured Local Plumbers"
  }];
  return <section id="request-this-service" data-nav="light" className="relative bg-background py-16 md:py-24 border-t border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={containerVariants} className="relative rounded-2xl bg-primary text-primary-foreground p-8 sm:p-12 md:p-16 shadow-xl overflow-hidden">
          {/* Subtle decorative background glow */}
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-accent/15 blur-3xl pointer-events-none" />
          <div className="absolute -left-20 -bottom-20 w-72 h-72 rounded-full bg-secondary/10 blur-3xl pointer-events-none" />

          <div className="relative z-10 text-center max-w-3xl mx-auto flex flex-col items-center">
            {/* Urgent Dispatch Badge */}
            <div className="inline-flex items-center gap-2 rounded-full bg-accent px-4 py-1.5 text-xs sm:text-sm font-bold tracking-wide uppercase text-accent-foreground shadow-xs mb-6">
              <Clock className="size-4" />
              <span>Available 24/7 in Jonesboro, GA</span>
            </div>

            {/* Main Action Headline */}
            <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-primary-foreground leading-tight">
              Need a Plumber in Jonesboro?
            </h2>

            {/* Direct Problem-Solving Subcopy */}
            <p className="mt-4 text-base sm:text-lg text-primary-foreground/90 leading-relaxed max-w-2xl">
              Tell us what's happening and request the appropriate plumbing service.
            </p>

            {/* Core CTA Action Cluster */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 w-full max-w-md sm:max-w-none">
              {/* Emergency Call Now Pill Button */}
              <Button asChild size="lg" className="w-full sm:w-auto h-14 px-8 rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-base shadow-lg transition-[background-color,box-shadow]">
                <a href="tel:4708479076" className="flex items-center justify-center gap-2.5">
                  <Phone className="size-5" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>

              {/* Request Service Form Navigation Button */}
              <Button asChild size="lg" variant="outline" className="w-full sm:w-auto h-14 px-8 rounded-full bg-background text-foreground hover:bg-muted font-semibold text-base border-border transition-[background-color,box-shadow]">
                <Link to="/contact/" className="flex items-center justify-center gap-2.5">
                  <CalendarCheck className="size-5 text-primary" />
                  <span>Request Service Online</span>
                </Link>
              </Button>
            </div>

            {/* Trust and Reliability Checkpoints */}
            <div className="mt-10 pt-8 border-t border-primary-foreground/15 grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
              {guarantees.map((item, index) => <div key={item.text} data-index={index} className="flex items-center justify-center sm:justify-start gap-2 text-xs sm:text-sm text-primary-foreground/85 font-medium">
                  <CheckCircle2 className="size-4 text-accent shrink-0" />
                  <span>{item.text}</span>
                </div>)}
            </div>

            {/* Local presence footer note */}
            <div className="mt-6 flex items-center justify-center gap-2 text-xs text-primary-foreground/75">
              <MapPin className="size-3.5 text-accent" />
              <span>Serving Jonesboro, GA and surrounding Clayton County communities</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}